import { LookerConnectionEmailTriggerOptions } from "@coeff/api";
import { COLORS } from "@coeff/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useRef, useState } from "react";
import styled from "styled-components";

import { Typography } from "../../../common";
import { Tooltip } from "../../../common";
import { Button } from "../../../common/Button";
import { Card } from "../../../common/Card";
import { useApiClient, useEventManagerContext } from "../../../globals/contexts";
import { QueryCacheKey } from "../../context/AuthContext";

import { StyledAnchor } from "./AuthorizeLookerContentV2";

const CardSectionSubheader = styled(Typography)`
  color: ${COLORS.black85};
  text-align: center;
  margin-top: 15px;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
`;

const CardPanel = styled.div`
  padding: 8px;
  background: ${COLORS.white};
  border-radius: 4px;
  margin-top: 15px;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;

  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const REQUEST_ADMIN_MESSAGE = `Hey, I’ve been using Coefficient to easily work with our company data in Sheets and need your
help connecting to Looker (it requires information that only admins can access). Can you please
help me by entering the information on this page?`;

const LOGIN_INTENT = "intent";

type RequestAccessStepProps = {
  event_props: {
    first_name?: string;
    last_name?: string;
  };
  webappBaseUrl?: string;
};

export const RequestAccessStep: React.FC<RequestAccessStepProps> = ({
  event_props,
  webappBaseUrl = "",
}) => {
  const [message, setMessage] = useState<"copy" | "copied">("copy");

  const api = useApiClient();

  const { track } = useEventManagerContext();

  const once = useRef(false);

  const { data: uuid, isLoading } = useQuery(
    [QueryCacheKey.LOOKER_REQUEST_URL],
    () => api.createLookerUrlBasedAuth(),
    {
      select: response => response.data.attached_uuid,
      cacheTime: 0,
    }
  );

  const search = new URLSearchParams();
  search.set("uuid", uuid!);
  search.set(
    LOGIN_INTENT,
    encodeURIComponent(
      JSON.stringify({
        message: `${event_props.first_name} ${event_props.last_name} shared this page with you to help connect Looker to Sheets using Coefficient`,
        description: `Connecting to Looker requires your client ID & secret (that only admins can access). Enter it below and we’ll notify ${event_props.first_name} once it’s complete.`,
      })
    )
  );

  const link = `${webappBaseUrl}/request-looker/start?${search.toString()}`;

  const sendmail = useMutation({
    mutationFn: async (uuid: string) => {
      const response = await api.triggerLookerConnEmail({
        trigger_looker_conn_email_request: {
          attached_uuid: uuid,
          trigger_type: LookerConnectionEmailTriggerOptions.UrlCopied,
        },
      });
      return response;
    },
  });

  const onCopy = () => {
    if (!uuid) {
      return;
    }

    const textToCopy = `${REQUEST_ADMIN_MESSAGE} \n\n${link}`;

    const tempTextArea = document.createElement("textarea");
    tempTextArea.value = textToCopy;
    document.body.appendChild(tempTextArea);

    tempTextArea.select();
    document.execCommand("copy");

    document.body.removeChild(tempTextArea);

    setMessage("copied");
    setTimeout(() => setMessage("copy"), 2000);
    if (!once.current) {
      once.current = true;
      sendmail.mutate(uuid);
      track("looker_setup_shared", {
        looker_request_access_uuid: uuid,
        looker_version: 2,
      });
    }
  };

  return (
    <Card style={{ padding: "15px 10px", margin: "15px", background: COLORS.coeblue11 }}>
      <Typography
        fontWeight={700}
        fontSize={14}
        lineHeight={"22px"}
        color="textPrimary"
        align="center"
      >
        Copy & share with your admin
      </Typography>
      <CardSectionSubheader>
        Share the link below with your Looker admin to connect on your behalf. We'll email you once
        it's complete.
      </CardSectionSubheader>
      <CardPanel>
        <Typography
          fontWeight={500}
          color="textSecondary"
          fontSize={14}
          style={{
            marginRight: 15,
            textAlign: "justify",
          }}
        >
          {REQUEST_ADMIN_MESSAGE}
        </Typography>
        {isLoading ? (
          "Loading..."
        ) : (
          <StyledAnchor href={link} target="_blank">
            Enable Looker Config
          </StyledAnchor>
        )}
        <Tooltip title={message} placement="top">
          <Button style={{ width: "100%" }} type="primary" onClick={onCopy}>
            {sendmail.isLoading ? "Copying..." : "Copy message & link"}
          </Button>
        </Tooltip>
      </CardPanel>
    </Card>
  );
};
